/**
 * JointAttentionDetector — 4꼭지 (Joint Attention / GazeTarget360)
 *
 * 2명 이상의 얼굴이 동시에 카메라에 잡혔을 때, 각자의 헤드 포즈
 * 벡터(yaw, pitch + nose-tip 원점)를 이미지 평면에 투사하여 다음을
 * 판정한다:
 *   1. Mutual gaze     — 두 사람이 서로의 얼굴 영역을 응시
 *   2. Joint attention — 두 사람이 같은 ROI(책상 위 시험지 등)를 응시
 *   3. Individual / Disengaged — 그 외
 *
 * 출력은 단정 boolean이 아닌 sustain duration + confidence를 함께
 * 보고한다 (AI Proctor 6원칙 #2 — 오탐 전제). 1초 sliding window 누적.
 *
 * 본 모듈은 알고리즘만 담당하며 face 입력 자체는 별도 hook
 * (useMultiFaceLandmarker)에서 공급된다. 단일 얼굴 파이프라인
 * (useFaceLandmarker, numFaces:1)과 분리되어 1인 시험감독 시나리오에
 * 영향이 없다.
 */

export interface BBox {
  /** Normalized [0,1] coordinates. */
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FaceInput {
  /** Stable identifier across frames (bbox IoU + keypoint embedding based). */
  id: string;
  /** Face bounding box (normalized). */
  bbox: BBox;
  /** Head yaw in degrees (+ = looking right). */
  yawDeg: number;
  /** Head pitch in degrees (+ = looking up). */
  pitchDeg: number;
  /** Nose tip in normalized [0,1] image coords — gaze ray origin. */
  noseTip: { x: number; y: number };
  /** Optional confidence of the face / pose estimate [0,1]. */
  confidence?: number;
}

export type GazeTarget =
  | { kind: 'partner_face'; partnerId: string }
  | { kind: 'common_roi'; roiId: string }
  | { kind: 'elsewhere' };

export interface FaceGazeAssignment {
  faceId: string;
  target: GazeTarget;
  /** Where the gaze ray landed on the image plane (normalized). */
  rayLanding: { x: number; y: number };
}

export interface MutualGazeEvent {
  /** Raw per-frame state: both faces register partner_face this frame.
   *  Kept for cumulative-metric integration, which is itself an integral
   *  and so naturally robust to single-frame coincidences. */
  active: boolean;
  /** Debounced flag for display / logging: true once the pair has
   *  accumulated ≥ MUTUAL_SUSTAINED_MIN_MS of mutual gaze within the
   *  window. A single-frame angular coincidence never sets this, so the
   *  HUD badge and narrator don't flicker on noise. */
  sustained: boolean;
  faceAId: string;
  faceBId: string;
  /** Sustained duration in ms within the window. */
  durationMs: number;
  confidence: number;
  /** Phase 2-D — soft mutual-gaze score [0,1] this frame. min of the
   *  two per-face partner-gaze scores. 0.5 ≡ angle ≈ threshold/2. */
  softScore?: number;
  /** Per-face angular distance (degrees) from gaze ray to partner. */
  angleADeg?: number;
  angleBDeg?: number;
}

export interface JointAttentionEvent {
  active: boolean;
  roiId: string;
  participantIds: string[];
  durationMs: number;
  confidence: number;
}

export interface Roi {
  id: string;
  bbox: BBox;
}

export type DyadState =
  | 'absent'        // < 2 faces — dyad cannot be measured
  | 'mutual'        // mutual gaze active
  | 'joint'         // ≥ 2 faces on the same ROI
  | 'individual'    // exactly one engaged (partner-gaze or ROI)
  | 'disengaged'    // both elsewhere
  ;

export interface JointAttentionFrame {
  /** Per-face gaze target classification this frame. */
  assignments: FaceGazeAssignment[];
  /** Active mutual gaze pairs within the sliding window. */
  mutualGazes: MutualGazeEvent[];
  /** Active joint attention groups (≥2 faces on the same ROI). */
  jointAttentions: JointAttentionEvent[];
  /** Tracked face count this frame. */
  faceCount: number;
  /** Cumulative dyadic-research metrics since session start (Phase 2-A). */
  metrics: JointAttentionMetrics;
  /** Phase 2-C — single dominant state this frame, used for the
   *  Gantt-timeline visualisation. Priority: mutual > joint > individual
   *  > disengaged. `absent` when faceCount < 2. */
  dyadState: DyadState;
}

/**
 * Dyadic metrics accumulated since the session began. Computed only
 * while ≥ 2 faces are tracked — otherwise the dyad is undefined.
 *
 * Conventions: durations in ms; indices in [0,1] as fraction of dyadic
 * observation time. `asymmetryAB` ∈ [-1,1]: -1 = only A→B partner
 * gazes, +1 = only B→A.
 */
export interface JointAttentionMetrics {
  /** ms with both faces visible (denominator for indices). */
  dyadObservedMs: number;
  /** ms with both faces gazing same ROI (Joint Attention Index numerator). */
  jointAttentionMs: number;
  /** ms with mutual gaze (Mutual Gaze Index numerator). */
  mutualGazeMs: number;
  /** ms with neither gazing partner or shared ROI. */
  disengagedMs: number;
  /** Joint Attention Index = jointAttentionMs / dyadObservedMs. */
  jai: number;
  /** Mutual Gaze Index = mutualGazeMs / dyadObservedMs. */
  mgi: number;
  /** Disengagement rate = disengagedMs / dyadObservedMs. */
  disengagementRate: number;
  /** Per-face count of times that face was a partner-gaze initiator
   *  (looked at partner first; partner followed within syncWindowMs). */
  initiations: Record<string, number>;
  /** Asymmetry A vs B for the most-observed pair, ∈ [-1, 1]. Positive =
   *  second id leads, negative = first id leads. NaN if no pair. */
  initiationAsymmetry: number;
  /** Mean sync-lag (ms) for joint-ROI follow events (one face starts
   *  gazing ROI, other follows). NaN if no events. */
  meanSyncLagMs: number;
  /** Total joint-ROI follow events counted. */
  syncLagSamples: number;
}

// ─── Internal sample types ──────────────────────────────────────

interface TimedFlag { t: number; on: boolean }

// ─── Detector ──────────────────────────────────────────────────

/**
 * Stateful detector. Call `update(faces, rois, now)` per frame
 * (recommend 15–30 fps).
 *
 * @param windowMs duration of the sliding window for sustain measurement.
 *   Default 1000 ms.
 * @param rayDegreesPerImage how many degrees of head yaw/pitch map to
 *   one full image width/height. Default 60 (≈ webcam FoV).
 */
/**
 * Phase 2-D — soft gaze scoring config. Threshold is the angle (deg) at
 * which "partner gaze" score drops to 0; the score ramps linearly from
 * 1 at 0° to 0 at threshold. `activeAtScore` is the lower-bound score
 * to register the partner gaze for `active`/`assignments`.
 *
 * Defaults are conservative: 25° cone, active at score 0.5 (≡ 12.5°).
 * The previous binary point-in-bbox check was both too strict (flapped
 * at bbox edges) and too lenient (a gaze 5px to the side of the bbox
 * silently failed). Angular distance is camera-FoV-independent and
 * smooth.
 */
export interface SoftGazeConfig {
  /** Angle (deg) at which partner gaze score reaches 0. Default 25. */
  thresholdDeg: number;
  /** Min score required to mark partner_face / mutual gaze. Default 0.5. */
  activeAtScore: number;
  /** Assumed camera horizontal FoV in degrees — used to translate image
   *  coords to a 3D camera ray. Default 60 (typical webcam). */
  cameraFovDeg: number;
  /** Assumed video aspect ratio (w/h). Default 16/9. */
  aspectRatio: number;
}

/**
 * Minimum accumulated mutual-gaze on-time (ms) within the sliding window
 * before a pair is reported as `sustained`. A single-frame angular
 * coincidence (~16-33 ms) falls far below this, so it never surfaces as an
 * "ACTIVE" badge or feeds the long-window narrator; genuine mutual gaze
 * (held or repeated within the window) crosses it. Debounced counterpart
 * to the raw per-frame `active` flag, consistent with the module's
 * "sustain duration over curt boolean" design (see header).
 */
const MUTUAL_SUSTAINED_MIN_MS = 200;

export class JointAttentionDetector {
  private readonly windowMs: number;
  private readonly rayScale: number;
  /** Time window (ms) within which a partner gaze counts as "following"
   *  for initiation-asymmetry / sync-lag attribution. */
  private readonly syncWindowMs: number;
  private readonly soft: SoftGazeConfig;
  // Per-pair / per-(face,roi) ring of on/off flags.
  private mutualRings: Map<string, TimedFlag[]> = new Map();
  private roiRings: Map<string, TimedFlag[]> = new Map();

  // ─── Cumulative dyadic state (Phase 2-A) ───────────────────────
  private lastT: number | null = null;
  private dyadObservedMs = 0;
  private jointAttentionMs = 0;
  private mutualGazeMs = 0;
  private disengagedMs = 0;
  // Per-face "first looked at partner at t" → enables initiator-vs-
  // follower attribution. Reset when the face's gaze leaves partner.
  private partnerGazeOnsetMs: Map<string, number> = new Map();
  private initiations: Map<string, number> = new Map();
  // First-seen times for ROI gaze (per face, per roi) → sync-lag.
  private roiGazeOnsetMs: Map<string, number> = new Map();
  // Last completed sync-lag samples (face followed partner to ROI).
  private syncLagSum = 0;
  private syncLagSamples = 0;
  // Per-face partner-gaze run tracking for cumulative observed time.
  private prevAssignments: Map<string, GazeTarget> = new Map();

  constructor(
    windowMs = 1000,
    rayDegreesPerImage = 60,
    syncWindowMs = 3000,
    softConfig?: Partial<SoftGazeConfig>,
  ) {
    this.windowMs = windowMs;
    this.rayScale = rayDegreesPerImage;
    this.syncWindowMs = syncWindowMs;
    this.soft = {
      thresholdDeg: softConfig?.thresholdDeg ?? 25,
      activeAtScore: softConfig?.activeAtScore ?? 0.5,
      cameraFovDeg: softConfig?.cameraFovDeg ?? 60,
      aspectRatio: softConfig?.aspectRatio ?? 16 / 9,
    };
  }

  reset(): void {
    this.mutualRings.clear();
    this.roiRings.clear();
    this.lastT = null;
    this.dyadObservedMs = 0;
    this.jointAttentionMs = 0;
    this.mutualGazeMs = 0;
    this.disengagedMs = 0;
    this.partnerGazeOnsetMs.clear();
    this.initiations.clear();
    this.roiGazeOnsetMs.clear();
    this.syncLagSum = 0;
    this.syncLagSamples = 0;
    this.prevAssignments.clear();
  }

  update(
    faces: FaceInput[],
    rois: Roi[],
    now: number,
  ): JointAttentionFrame {
    // 2D landing (used for the rayLanding overlay + ROI tests).
    const landings: Array<{ face: FaceInput; landing: { x: number; y: number } }> =
      faces.map(f => ({ face: f, landing: this.project(f) }));

    // Phase 2-D — 3D positions + gaze directions for soft scoring.
    const face3D = new Map<string, { pos: Vec3; dir: Vec3 }>();
    for (const f of faces) {
      face3D.set(f.id, {
        pos: this.faceCameraPos(f),
        dir: this.gazeDir(f.yawDeg, f.pitchDeg),
      });
    }

    // Per-(face, partner) angular score, kept for HUD/metrics.
    const partnerScore = new Map<string, { partnerId: string; score: number; angleDeg: number }>();
    for (const f of faces) {
      let bestId: string | null = null;
      let bestScore = 0;
      let bestAngle = 180;
      const me = face3D.get(f.id)!;
      for (const g of faces) {
        if (g.id === f.id) continue;
        const other = face3D.get(g.id)!;
        const toPartner = normalize(subtract(other.pos, me.pos));
        const angle = angleBetweenDeg(me.dir, toPartner);
        const s = Math.max(0, 1 - angle / this.soft.thresholdDeg);
        if (s > bestScore) { bestScore = s; bestId = g.id; bestAngle = angle; }
      }
      if (bestId) partnerScore.set(f.id, { partnerId: bestId, score: bestScore, angleDeg: bestAngle });
    }

    // Per-face assignment: highest-scoring partner over activeAtScore;
    // else point-in-bbox ROI; else elsewhere.
    const assignments: FaceGazeAssignment[] = landings.map(({ face, landing }) => {
      const ps = partnerScore.get(face.id);
      if (ps && ps.score >= this.soft.activeAtScore) {
        return {
          faceId: face.id,
          target: { kind: 'partner_face', partnerId: ps.partnerId },
          rayLanding: landing,
        };
      }
      const roi = rois.find(r => pointInBBox(landing, r.bbox));
      if (roi) {
        return {
          faceId: face.id,
          target: { kind: 'common_roi', roiId: roi.id },
          rayLanding: landing,
        };
      }
      return { faceId: face.id, target: { kind: 'elsewhere' }, rayLanding: landing };
    });

    // Mutual gaze: both sides registered partner_face on the other.
    const partnerLookup = new Map<string, string>();
    for (const a of assignments) {
      if (a.target.kind === 'partner_face') partnerLookup.set(a.faceId, a.target.partnerId);
    }
    const activePairKeys = new Set<string>();
    for (const [aId, bId] of Array.from(partnerLookup.entries())) {
      if (partnerLookup.get(bId) === aId) {
        activePairKeys.add(pairKey(aId, bId));
      }
    }
    // Touch every pair that was previously tracked OR is active this frame.
    const allPairKeys = new Set<string>([
      ...Array.from(this.mutualRings.keys()),
      ...Array.from(activePairKeys),
    ]);
    for (const key of Array.from(allPairKeys)) {
      const ring = this.mutualRings.get(key) ?? [];
      ring.push({ t: now, on: activePairKeys.has(key) });
      this.prune(ring, now);
      if (ring.length === 0) this.mutualRings.delete(key);
      else this.mutualRings.set(key, ring);
    }

    // Update ROI rings. A (face, roi) is "on" iff that face's ray lands in the roi.
    const activeFaceRoiKeys = new Set<string>();
    for (const a of assignments) {
      if (a.target.kind === 'common_roi') {
        activeFaceRoiKeys.add(faceRoiKey(a.faceId, a.target.roiId));
      }
    }
    const allRoiKeys = new Set<string>([
      ...Array.from(this.roiRings.keys()),
      ...Array.from(activeFaceRoiKeys),
    ]);
    for (const key of Array.from(allRoiKeys)) {
      const ring = this.roiRings.get(key) ?? [];
      ring.push({ t: now, on: activeFaceRoiKeys.has(key) });
      this.prune(ring, now);
      if (ring.length === 0) this.roiRings.delete(key);
      else this.roiRings.set(key, ring);
    }

    // Aggregate mutual-gaze sustain + attach soft scores.
    const mutualGazes: MutualGazeEvent[] = [];
    for (const [key, ring] of Array.from(this.mutualRings.entries())) {
      const [a, b] = parsePairKey(key);
      const { onMs, confidence } = sustain(ring, this.windowMs);
      const sa = partnerScore.get(a);
      const sb = partnerScore.get(b);
      const softScore = sa && sb ? Math.min(sa.score, sb.score) : undefined;
      mutualGazes.push({
        active: activePairKeys.has(key),
        sustained: onMs >= MUTUAL_SUSTAINED_MIN_MS,
        faceAId: a,
        faceBId: b,
        durationMs: onMs,
        confidence,
        softScore,
        angleADeg: sa?.angleDeg,
        angleBDeg: sb?.angleDeg,
      });
    }

    // Aggregate joint-attention per ROI: ≥2 faces with sustained ROI gaze.
    const byRoi: Map<string, Array<{ face: string; onMs: number; conf: number; active: boolean }>> = new Map();
    for (const [key, ring] of Array.from(this.roiRings.entries())) {
      const { face, roi } = parseFaceRoiKey(key);
      const { onMs, confidence } = sustain(ring, this.windowMs);
      if (!byRoi.has(roi)) byRoi.set(roi, []);
      byRoi.get(roi)!.push({
        face,
        onMs,
        conf: confidence,
        active: activeFaceRoiKeys.has(key),
      });
    }
    const jointAttentions: JointAttentionEvent[] = [];
    for (const [roiId, members] of Array.from(byRoi.entries())) {
      const active = members.filter(m => m.active);
      if (active.length < 2) continue;
      jointAttentions.push({
        active: true,
        roiId,
        participantIds: active.map(m => m.face),
        durationMs: Math.min(...active.map(m => m.onMs)),
        confidence: Math.min(...active.map(m => m.conf)),
      });
    }

    // ─── Phase 2-A: cumulative metrics ──────────────────────────
    const dt = this.lastT != null ? Math.max(0, Math.min(now - this.lastT, 500)) : 0;
    const isDyad = faces.length >= 2;
    if (isDyad) {
      this.dyadObservedMs += dt;
      const anyMutual = mutualGazes.some(m => m.active);
      const anyJoint = jointAttentions.some(j => j.active);
      if (anyMutual) this.mutualGazeMs += dt;
      if (anyJoint) this.jointAttentionMs += dt;
      const anyEngaged = assignments.some(a => a.target.kind !== 'elsewhere');
      if (!anyEngaged) this.disengagedMs += dt;

      // Track partner-gaze onset per face — initiator is whoever's
      // partner-gaze onset preceded the other's by ≤ syncWindowMs and
      // the second face's onset happened while the first was still
      // looking at the partner.
      for (const a of assignments) {
        const prevTarget = this.prevAssignments.get(a.faceId);
        const nowPartnerGaze = a.target.kind === 'partner_face';
        const wasPartnerGaze = prevTarget?.kind === 'partner_face';
        if (nowPartnerGaze && !wasPartnerGaze) {
          // Onset for this face
          this.partnerGazeOnsetMs.set(a.faceId, now);
          // Was the partner already gazing the partner (us)? Then we're the follower.
          const partnerOnset = this.partnerGazeOnsetMs.get((a.target as { partnerId: string }).partnerId);
          if (partnerOnset != null && now - partnerOnset <= this.syncWindowMs) {
            // The partner became the initiator — credit them.
            const initiator = (a.target as { partnerId: string }).partnerId;
            this.initiations.set(initiator, (this.initiations.get(initiator) ?? 0) + 1);
          }
        } else if (!nowPartnerGaze && wasPartnerGaze) {
          this.partnerGazeOnsetMs.delete(a.faceId);
        }
      }

      // Sync-lag per ROI: when face A starts gazing ROI X, record onset.
      // When face B (≠ A) then starts gazing same ROI X within window,
      // emit a follow event with lag = onsetB - onsetA.
      for (const a of assignments) {
        const prevTarget = this.prevAssignments.get(a.faceId);
        const nowRoi = a.target.kind === 'common_roi'
          ? (a.target as { roiId: string }).roiId : null;
        const wasRoi = prevTarget?.kind === 'common_roi'
          ? (prevTarget as { roiId: string }).roiId : null;
        if (nowRoi && nowRoi !== wasRoi) {
          // Onset of gaze at this ROI
          const k = `${a.faceId}>${nowRoi}`;
          this.roiGazeOnsetMs.set(k, now);
          // Did anyone else already start this ROI within syncWindow?
          for (const [otherKey, otherOnset] of Array.from(this.roiGazeOnsetMs.entries())) {
            if (otherKey === k) continue;
            const [otherFace, otherRoi] = otherKey.split('>');
            if (otherRoi === nowRoi && otherFace !== a.faceId) {
              const lag = now - otherOnset;
              if (lag > 0 && lag <= this.syncWindowMs) {
                this.syncLagSum += lag;
                this.syncLagSamples += 1;
              }
            }
          }
        } else if (!nowRoi && wasRoi) {
          this.roiGazeOnsetMs.delete(`${a.faceId}>${wasRoi}`);
        }
      }

      // Snapshot prev for next tick
      this.prevAssignments.clear();
      for (const a of assignments) this.prevAssignments.set(a.faceId, a.target);
    } else {
      this.prevAssignments.clear();
      this.partnerGazeOnsetMs.clear();
      this.roiGazeOnsetMs.clear();
    }
    this.lastT = now;

    // Initiator asymmetry — only well-defined for exactly 2 faces.
    let asymmetry = Number.NaN;
    if (faces.length === 2) {
      const ids = faces.map(f => f.id).sort();
      const a = this.initiations.get(ids[0]) ?? 0;
      const b = this.initiations.get(ids[1]) ?? 0;
      const denom = a + b;
      asymmetry = denom > 0 ? (b - a) / denom : 0;
    }
    const denomMs = Math.max(1, this.dyadObservedMs);
    const metrics: JointAttentionMetrics = {
      dyadObservedMs: this.dyadObservedMs,
      jointAttentionMs: this.jointAttentionMs,
      mutualGazeMs: this.mutualGazeMs,
      disengagedMs: this.disengagedMs,
      jai: this.jointAttentionMs / denomMs,
      mgi: this.mutualGazeMs / denomMs,
      disengagementRate: this.disengagedMs / denomMs,
      initiations: Object.fromEntries(this.initiations.entries()),
      initiationAsymmetry: asymmetry,
      meanSyncLagMs: this.syncLagSamples > 0
        ? this.syncLagSum / this.syncLagSamples : Number.NaN,
      syncLagSamples: this.syncLagSamples,
    };

    // Phase 2-C — dominant single state for Gantt timeline.
    let dyadState: DyadState = 'absent';
    if (isDyad) {
      // Debounced flag for the dominant-state strip — the cumulative MGI
      // integral above keeps the raw per-frame `active` (a time-fraction
      // by definition); only the displayed state is debounced.
      const mg = mutualGazes.some(m => m.sustained);
      const ja = jointAttentions.some(j => j.active);
      const anyEngaged = assignments.some(a => a.target.kind !== 'elsewhere');
      const allEngaged = assignments.every(a => a.target.kind !== 'elsewhere');
      if (mg) dyadState = 'mutual';
      else if (ja) dyadState = 'joint';
      else if (anyEngaged && !allEngaged) dyadState = 'individual';
      else if (allEngaged) dyadState = 'individual';  // both engaged but not aligned
      else if (!anyEngaged) dyadState = 'disengaged';
    }

    return {
      assignments,
      mutualGazes,
      jointAttentions,
      faceCount: faces.length,
      metrics,
      dyadState,
    };
  }

  private project(f: FaceInput): { x: number; y: number } {
    // Treat yaw/pitch as angular displacement and translate the ray
    // origin (nose tip) by (yaw / rayScale) of the image width and
    // (-pitch / rayScale) of the image height. Clamped to [0, 1] so
    // the landing always sits on the image plane.
    const dx = f.yawDeg / this.rayScale;
    const dy = -f.pitchDeg / this.rayScale;
    return {
      x: clamp01(f.noseTip.x + dx),
      y: clamp01(f.noseTip.y + dy),
    };
  }

  /** Phase 2-D — back-project a face's nose tip from image coords into
   *  a 3D camera ray, then estimate depth from bbox width (heuristic:
   *  typical face ≈ 0.15 m wide, focal length from camera FoV). The
   *  result is in camera coords with the camera at origin looking down
   *  -Z. Used to compute angular distance between two faces.
   */
  private faceCameraPos(f: FaceInput): Vec3 {
    // Image normalised → unit-z pinhole ray
    const fovRad = (this.soft.cameraFovDeg * Math.PI) / 180;
    const halfTanH = Math.tan(fovRad / 2);
    const ar = this.soft.aspectRatio;
    // Bbox width is unavailable on FaceInput; approximate from nose-tip
    // depth proxy: faces near the camera have noseTip closer to centre
    // when looking straight at camera. We use a *constant* depth proxy
    // 1.0 because for the dyadic angular check what matters is the
    // *direction* from one face to the other, not absolute scale.
    const x = (f.noseTip.x * 2 - 1) * halfTanH;
    const y = -(f.noseTip.y * 2 - 1) * (halfTanH / ar);
    return { x, y, z: -1 };
  }

  /** Phase 2-D — head gaze unit vector in camera coords.
   *  yaw > 0 → look right (toward +X);
   *  pitch > 0 → look up (toward +Y);
   *  forward = -Z when both zero. */
  private gazeDir(yawDeg: number, pitchDeg: number): Vec3 {
    const y = (yawDeg * Math.PI) / 180;
    const p = (pitchDeg * Math.PI) / 180;
    return {
      x: Math.sin(y) * Math.cos(p),
      y: Math.sin(p),
      z: -Math.cos(y) * Math.cos(p),
    };
  }

  private prune(ring: TimedFlag[], now: number): void {
    const cutoff = now - this.windowMs;
    while (ring.length > 0 && ring[0].t < cutoff) ring.shift();
  }
}

// ─── Helpers ───────────────────────────────────────────────────

interface Vec3 { x: number; y: number; z: number }

function subtract(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}
function normalize(v: Vec3): Vec3 {
  const m = Math.hypot(v.x, v.y, v.z);
  return m > 1e-9 ? { x: v.x / m, y: v.y / m, z: v.z / m } : { x: 0, y: 0, z: 0 };
}
function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}
function angleBetweenDeg(a: Vec3, b: Vec3): number {
  const d = Math.max(-1, Math.min(1, dot(a, b)));
  return (Math.acos(d) * 180) / Math.PI;
}

function pointInBBox(p: { x: number; y: number }, b: BBox): boolean {
  return p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function pairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

function parsePairKey(k: string): [string, string] {
  const [a, b] = k.split('|');
  return [a, b];
}

function faceRoiKey(face: string, roi: string): string {
  return `${face}>${roi}`;
}

function parseFaceRoiKey(k: string): { face: string; roi: string } {
  const [face, roi] = k.split('>');
  return { face, roi };
}

function sustain(ring: TimedFlag[], windowMs: number): { onMs: number; confidence: number } {
  if (ring.length < 2) return { onMs: 0, confidence: 0 };
  let onMs = 0;
  for (let i = 1; i < ring.length; i++) {
    if (ring[i - 1].on && ring[i].on) {
      onMs += ring[i].t - ring[i - 1].t;
    }
  }
  // Confidence saturates at 80% of the window held continuously.
  const ratio = onMs / (windowMs * 0.8);
  return { onMs, confidence: Math.min(1, ratio) };
}
